import { deliveryTerritory, RouteRef } from './delivery-territory';
import { TruckRouteView } from './plan-result';
import { basePlanOrderId } from './route-optimization.mapper';

/**
 * El gestor de confirmación: revisar y personalizar el orden de visita ANTES de
 * persistirlo.
 *
 * Existe porque `Confirmar` guardaba el orden del optimizador tal cual, y el
 * operador conoce cosas que el motor no —un cliente que solo recibe temprano, un
 * portón que a cierta hora no abre—. El gestor le da la última palabra: dejar el
 * orden del motor, o reordenar dentro de cada ruta.
 *
 * Límites deliberados:
 * - **Entre rutas, solo con territorio compartido.** Un pedido puede moverse a
 *   otra ruta ÚNICAMENTE si el cliente y esa ruta comparten al menos un
 *   territorio (cruce por `id`, ver `delivery-territory.ts`): una ruta no
 *   entrega donde no visita. El movimiento no recalcula pesos ni tiempos — esas
 *   métricas siguen siendo las del plan.
 * - **Sin tiempos en el orden personalizado, hasta que se recalcule.** Las llegadas
 *   y tramos que trae el plan pertenecen al orden del optimizador; mostrarlos sobre
 *   un orden manual sería mentir con precisión de minutos.
 *
 *   Esto además afirmaba que *"no existe endpoint para reevaluar un orden fijo"*, y
 *   era falso: `POST /sort` está en el cliente HTTP desde antes que este gestor. Lo
 *   que sí es cierto es que **reordena** —recibe paradas y devuelve el mejor
 *   recorrido para ellas—, así que recalcular es devolverle la ruta al motor y no
 *   cronometrar una secuencia fija. Ver `route-recalc.ts`.
 *
 * Módulo puro: entran las rutas del plan, salen órdenes y el cuerpo del POST.
 * Sin HTTP, sin Angular.
 */

/** Una parada del gestor: lo mínimo para identificarla y arrastrarla. */
export interface ReviewStop {
  /** `order_id` del PLAN, con su sufijo de división si lo trae. */
  orderId: string;
  name: string;
  address: string;
  weightLb: number;
  /**
   * `true` si el operador la INCORPORÓ a mano: un pedido que no entró al plan y
   * que por lo tanto no está en el `original` de ninguna ruta.
   *
   * Vive en la parada y no en un `Set` del diálogo porque es un atributo de ELLA:
   * la acompaña al reordenarla y desaparece con ella al restaurar. Un conjunto
   * aparte se desincronizaría con el primer movimiento.
   *
   * Opcional: una parada del plan simplemente no lo trae.
   */
  injected?: boolean;
}

/** Una ruta en revisión, con su orden original congelado para poder volver. */
export interface ReviewRoute {
  subRouteId: string;
  /** Nombre de la RUTA. Nunca el territorio: ese va en `territoryName`. */
  label: string;
  /** Territorio de la ruta, para mostrarlo junto al nombre y no en su lugar. */
  territoryName?: string;
  color: string;
  /** El orden del optimizador. No se muta: es la vara de `custom` y del reset. */
  original: readonly ReviewStop[];
  /** El orden vigente, el que se confirma. */
  stops: ReviewStop[];
  /** Capacidad del camión por viaje (lb), como la usó el plan. */
  capacityLb: number;
  /** Viajes que el plan le dio al camión (1 = sin recargas). */
  trips: number;
  /** `true` si `stops` difiere del optimizador. Vuelve a `false` si el arrastre lo deja igual. */
  custom: boolean;
  /**
   * Si la tarjeta muestra sus paradas. Nace **cerrada**, como las tarjetas del
   * resultado: 17 rutas × ~14 filas arrastrables construidas de entrada vuelven
   * pesado el gestor y entierran el panorama de qué rutas hay.
   */
  open: boolean;
}

/** Fila del cuerpo de `POST trip/confirm-optimized-routes`. */
export interface ConfirmRoutePayload {
  subRouteId: string;
  stops: Array<{ orderId: string; sequence: number }>;
}

/**
 * Rutas del plan → rutas revisables.
 *
 * Solo las que salieron de una sub-ruta real: los grupos por municipio o
 * residuales no tienen `Trip` que crear en `ave-trip`, así que quedan fuera del
 * gestor igual que quedan fuera de la confirmación.
 */
export function buildReviewRoutes(trucks: readonly TruckRouteView[]): ReviewRoute[] {
  return (trucks ?? [])
    .filter((t) => !!t.subRouteId)
    .map((t) => {
      const stops: ReviewStop[] = t.stops.map((s) => ({
        orderId: s.orderId,
        name: s.name,
        address: s.address,
        weightLb: s.weightLb,
      }));
      return {
        subRouteId: t.subRouteId as string,
        label: t.label,
        // El territorio va AL LADO del nombre de la ruta, no en su lugar: los dos
        // comparten número y se distinguen por el prefijo, así que un valor suelto
        // es indistinguible del otro concepto.
        territoryName: t.territoryName,
        color: t.color,
        original: stops.map((s) => ({ ...s })),
        stops,
        capacityLb: t.capacityLb ?? 0,
        trips: Math.max(1, t.trips ?? 1),
        custom: false,
        open: false,
      };
    });
}

/* ------------- El orden editado, entre aperturas del diálogo ------------- *
 *
 * Cerrar el modal destruye el componente, así que lo editado en el gestor moría
 * ahí: la foto de la corrida guardaba `truckViews` (el orden del MOTOR) y hasta
 * cuál tile del gestor estaba seleccionado, pero no el orden que el operador
 * dejó. Al reabrir, `confirm()` reconstruía desde el plan y todo volvía sin la
 * marca de `custom` — la pantalla quedaba indistinguible de un gestor que nunca
 * se editó, y se confirmaba el orden del motor creyendo confirmar el propio.
 *
 * Lo que viaja en la foto es el DELTA y no las rutas: dos representaciones del
 * mismo orden divergen, y acá divergir significa mostrar una y confirmar la otra.
 * El plan sigue siendo la única fuente de las rutas; la foto solo lleva la
 * decisión del operador.
 */

/** Por sub-ruta, la secuencia de `orderId` que el operador dejó. */
export type ReviewOrderDelta = Record<string, string[]>;

/**
 * Lo editado, listo para guardar.
 *
 * **Solo las rutas tocadas.** Una ruta en el orden del motor se reconstruye
 * igual desde `truckViews`, así que guardarla sería ruido — y con solo las
 * tocadas el dato se vuelve auto-descriptivo: una ruta ausente significa "quedó
 * como la propuso el motor".
 *
 * `custom` alcanza como criterio porque se recalcula contra `original` y un
 * movimiento entre rutas lo enciende en LAS DOS puntas, así que cualquier ruta
 * cuyo contenido u orden cambió está acá.
 *
 * El `orderId` va **CRUDO**, sin `basePlanOrderId`. Esa normalización es del
 * cuerpo del confirm, y reusarla para persistir haría indistinguibles `a__s1` y
 * `a__s2`: el orden de una visita dividida no se podría recomponer.
 */
export function reviewOrderDelta(routes: readonly ReviewRoute[]): ReviewOrderDelta {
  const delta: ReviewOrderDelta = {};
  for (const route of routes ?? []) {
    if (!route.custom) continue;
    delta[route.subRouteId] = route.stops.map((s) => s.orderId);
  }
  return delta;
}

/**
 * Re-aplica lo editado sobre las rutas recién reconstruidas del plan.
 *
 * **Global y no por ruta**, porque un movimiento entre rutas exige SACAR la
 * parada de donde el plan la puso. Aplicar solo la punta destino la dejaría en
 * las dos: una visita que dos camiones creen suya.
 *
 * Tres pasos:
 * 1. índice `orderId → { parada, ruta dueña }` del plan;
 * 2. reclamos del delta, solo de ids que el índice conoce y rutas que existen;
 * 3. reparto: cada ruta reclamante recibe sus paradas en el orden del delta, y
 *    las que nadie reclamó se quedan donde el plan las puso, en su orden.
 *
 * Los tres casos degradados son explícitos:
 *
 * - un `orderId` del delta que el plan ya no trae se intenta reconstruir con
 *   `resolveMissing` —así vuelven los pedidos incorporados a mano— y **se ignora** si
 *   tampoco ahí existe;
 * - una parada que la lista de SU ruta no nombra **se retiró a propósito** y no vuelve;
 * - una ruta **ausente** del delta conserva todas sus paradas: ahí "no la menciona"
 *   sigue significando "no se tocó esta ruta".
 *
 * Los dos últimos eran uno solo hasta que retirar una parada fue posible. Mientras no
 * lo era, una ausencia solo podía ser un accidente y conservarla era lo correcto.
 */
export function applyReviewOrderDelta(
  routes: ReviewRoute[],
  delta: ReviewOrderDelta | undefined,
  /**
   * De dónde sacar una parada que el PLAN no trae.
   *
   * Un pedido incorporado a mano desde los que no entraron está en el delta —los
   * `orderId` van crudos— pero no en `truckViews`, así que sin esto se perdía en
   * silencio al reabrir: la promesa de "lo editado sobrevive a cerrar el modal" tenía
   * una excepción que nadie había decidido, era el límite de la restauración.
   *
   * Se inyecta, como `canReceive` en el reclamo del orden guardado: este módulo no sabe
   * de candidatos ni de territorios. Devolver `undefined` es la respuesta correcta
   * tanto para "no lo tengo" como para "no puede ir a esa ruta" — el módulo no
   * necesita distinguirlas, solo saltear el id.
   */
  resolveMissing?: (orderId: string, subRouteId: string) => ReviewStop | undefined,
): void {
  if (!routes?.length || !delta) return;
  const entries = Object.entries(delta);
  if (!entries.length) return;

  const byId = new Map<string, ReviewStop>();
  for (const route of routes) {
    for (const stop of route.stops) byId.set(stop.orderId, stop);
  }

  // Qué ruta reclama cada parada. Un id repetido en dos listas del delta no
  // debería existir; si existiera, gana el primer reclamo y el segundo lo ignora
  // el reparto — nunca se duplica.
  const claimedBy = new Map<string, string>();
  const routeIds = new Set(routes.map((r) => r.subRouteId));
  for (const [subRouteId, ids] of entries) {
    if (!routeIds.has(subRouteId)) continue;
    for (const id of ids ?? []) {
      if (claimedBy.has(id)) continue;
      if (!byId.has(id)) {
        // No está en el plan: puede ser un pedido incorporado a mano. Si el llamador
        // sabe reconstruirlo —y la ruta puede recibirlo—, entra al índice como una
        // parada más y el reparto de abajo lo ubica en su posición del delta.
        const recuperada = resolveMissing?.(id, subRouteId);
        if (!recuperada) continue;
        byId.set(id, recuperada);
      }
      claimedBy.set(id, subRouteId);
    }
  }

  for (const route of routes) {
    const enDelta = routeIds.has(route.subRouteId) && !!delta[route.subRouteId];
    const ids = enDelta ? delta[route.subRouteId] : undefined;
    // Una ruta EN el delta declara su CONTENIDO completo: lo que su lista no nombra se
    // RETIRÓ a propósito, y devolverlo desharía esa decisión sin avisar —al final de la
    // ruta, encima, alterando también el orden—.
    //
    // Una ruta AUSENTE del delta quedó como el optimizador la propuso y conserva todo:
    // ahí "el delta no la menciona" sigue significando "no se tocó esta ruta".
    //
    // Antes las dos se trataban igual, y era correcto: retirar no existía, así que una
    // parada ausente de una lista solo podía ser un accidente. La función que permite
    // declararlo se agregó después.
    const kept = enDelta
      ? []
      : route.stops.filter((s) => (claimedBy.get(s.orderId) ?? route.subRouteId) === route.subRouteId);
    if (!ids?.length) {
      // Con `enDelta` y lista vacía la ruta queda vacía: vaciarla también es una
      // decisión, y antes caía acá y la dejaba intacta.
      route.stops = kept;
      continue;
    }
    const placed = new Set<string>();
    const ordered: ReviewStop[] = [];
    for (const id of ids) {
      if (claimedBy.get(id) !== route.subRouteId || placed.has(id)) continue;
      const stop = byId.get(id);
      if (!stop) continue;
      ordered.push(stop);
      placed.add(id);
    }
    // Las propias que el delta no nombró van después, sin perderse.
    route.stops = [...ordered, ...kept.filter((s) => !placed.has(s.orderId))];
  }

  // Red de seguridad, ACOTADA a las rutas ausentes del delta.
  //
  // Su premisa era: "una parada perdida saca del día una entrega que nadie declaró como
  // no entregada". Dejó de ser cierta para una ruta del delta — retirar es exactamente
  // esa declaración — así que ahí la red no puede hacer su trabajo: no distingue
  // "retirada" de "perdida", y actuar sería deshacer la decisión.
  //
  // Su función se mudó, y es mejor: una parada que se caiga NO desaparece del día,
  // aterriza en el pool de no asignados —que es derivado—, se cuenta y se pregunta al
  // confirmar. La red existía porque antes no había adónde caer.
  //
  // Para una ruta ausente del delta queda como chequeo residual —ahí nada debería
  // perderse, se reconstruye del plan sin tocar— y cuesta un recorrido que ya se hacía.
  const present = new Set<string>();
  for (const route of routes) {
    for (const stop of route.stops) present.add(stop.orderId);
  }
  for (const route of routes) {
    if (routeIds.has(route.subRouteId) && delta[route.subRouteId]) continue;
    const missing = route.original.filter(
      (s) => !present.has(s.orderId) && !claimedBy.has(s.orderId),
    );
    if (missing.length) route.stops = [...route.stops, ...missing.map((s) => ({ ...s }))];
  }

  for (const route of routes) route.custom = !sameAsOriginal(route);
}

/** ¿El orden vigente es el del optimizador? Por identidad de paradas, en orden. */
function sameAsOriginal(route: ReviewRoute): boolean {
  return (
    route.stops.length === route.original.length &&
    route.stops.every((s, i) => s.orderId === route.original[i]?.orderId)
  );
}

/**
 * Mueve una parada dentro de su ruta. Fuera de rango es un no-op: un arrastre
 * que termina donde empezó, o un ↑ en la primera fila, no deben marcar nada.
 *
 * `custom` se recalcula contra el original y no se "enciende y queda": si el
 * usuario deshace a mano su propio cambio, la ruta vuelve a contar como del
 * optimizador — que es la verdad.
 */
export function moveReviewStop(route: ReviewRoute, from: number, to: number): void {
  if (from === to) return;
  if (from < 0 || to < 0 || from >= route.stops.length || to >= route.stops.length) return;
  const [moved] = route.stops.splice(from, 1);
  route.stops.splice(to, 0, moved);
  route.custom = !sameAsOriginal(route);
}

/* ---------- Incorporar un pedido que NO entró al plan ---------- *
 *
 * La primera parada del gestor que no viene de una ruta. Es un camino APARTE del
 * movimiento entre rutas, y no un parámetro de él, a propósito: `moveTargetsFor`
 * y `canRouteReceive` definen "una ruta no entrega donde no visita" para tres
 * llamadores —incluida la restauración de pertenencia desde el orden guardado, que
 * DEBE seguir bloqueando—, y un flag para saltarse la regla la volvería una
 * sugerencia en los tres a la vez.
 *
 * Que acá no se consulte el territorio no es un descuido sino la decisión: un
 * pedido pendiente no comparte territorio con NINGUNA ruta que haya planificado,
 * porque cae en la brecha si y solo si ninguno de sus territorios tiene flota.
 * Ver `pending-orders.ts`, que es quien ordena los destinos por desvío.
 */

/**
 * Incorpora una parada a una ruta, en `at` o al final si no se da.
 *
 * `custom` se recalcula: una parada de más nunca coincide con el original, así que
 * la ruta queda marcada sin que haga falta afirmarlo.
 */
export function addStopToRoute(route: ReviewRoute, stop: ReviewStop, at?: number): void {
  if (!route || !stop) return;
  const index = at === undefined ? route.stops.length : Math.max(0, Math.min(at, route.stops.length));
  route.stops.splice(index, 0, { ...stop, injected: true });
  route.open = true;
  route.custom = !sameAsOriginal(route);
}

/**
 * Retira una parada de su ruta. Devuelve la parada retirada, o `undefined` si el
 * índice no apuntaba a ninguna.
 *
 * No hay que "devolverla" a ninguna lista: el pool de pendientes se DERIVA de lo
 * que no está en ninguna ruta, así que retirarla la hace reaparecer sola.
 */
export function removeReviewStop(route: ReviewRoute, index: number): ReviewStop | undefined {
  if (!route || index < 0 || index >= route.stops.length) return undefined;
  const [removed] = route.stops.splice(index, 1);
  route.custom = !sameAsOriginal(route);
  return removed;
}

/** Cuántos pedidos se incorporaron a mano, en todas las rutas. Se declara antes de confirmar. */
export function injectedCount(routes: readonly ReviewRoute[]): number {
  return (routes ?? []).reduce((n, r) => n + r.stops.filter((s) => s.injected).length, 0);
}

/** Ruta destino válida para una parada, con el territorio compartido que la avala. */
export interface MoveTarget {
  route: ReviewRoute;
  /** Nombre(s) del territorio compartido, como los trae el pedido. */
  sharedTerritory: string;
}

/**
 * Rutas a las que la parada PUEDE moverse: las que comparten al menos un
 * territorio con el cliente (regla de negocio: 1 cliente ∈ N territorios,
 * 1 ruta visita N territorios; sin intersección no hay entrega posible).
 *
 * El cruce reutiliza `deliveryTerritory` — por `id`, nunca por nombre — así que
 * un pedido sin territorios, o una ruta cuyo maestro no declara ninguno, no
 * produce destinos: sin dato no se afirma compatibilidad.
 *
 * @param territoryRefsOf territorios del pedido (por id BASE de la orden).
 * @param declaredRefsOf territorios que declara el maestro de una sub-ruta.
 */
export function moveTargetsFor(
  routes: readonly ReviewRoute[],
  current: ReviewRoute,
  stop: ReviewStop,
  territoryRefsOf: (baseOrderId: string) => readonly RouteRef[] | undefined,
  declaredRefsOf: (subRouteId: string) => readonly RouteRef[] | undefined,
): MoveTarget[] {
  const orderRoutes = territoryRefsOf(basePlanOrderId(stop.orderId));
  if (!orderRoutes?.length) return [];
  const targets: MoveTarget[] = [];
  for (const route of routes ?? []) {
    if (route === current) continue;
    const shared = deliveryTerritory(orderRoutes, declaredRefsOf(route.subRouteId));
    if (shared.territory && !shared.mismatch) {
      targets.push({ route, sharedTerritory: shared.territory });
    }
  }
  return targets;
}

/**
 * ¿Esta ruta puede recibir esta parada? El MISMO cruce que decide los destinos del
 * arrastre, en una sola definición.
 *
 * Existe porque hay un segundo llamador: restaurar la pertenencia desde el orden
 * guardado también tiene que respetar la regla —una ruta no entrega donde no
 * visita— y replicar el cruce daría dos definiciones de "compatible" que divergen.
 * El síntoma sería que arrastrar permite lo que adoptar el orden guardado prohíbe,
 * o al revés.
 *
 * Sin datos NO se afirma compatibilidad: un pedido sin territorios, o una ruta cuyo
 * maestro no declara ninguno, no puede recibir nada.
 */
export function canRouteReceive(
  subRouteId: string,
  stop: ReviewStop,
  territoryRefsOf: (baseOrderId: string) => readonly RouteRef[] | undefined,
  declaredRefsOf: (subRouteId: string) => readonly RouteRef[] | undefined,
): boolean {
  const orderRoutes = territoryRefsOf(basePlanOrderId(stop.orderId));
  if (!orderRoutes?.length) return false;
  const shared = deliveryTerritory(orderRoutes, declaredRefsOf(subRouteId));
  return !!shared.territory && !shared.mismatch;
}

/**
 * Mueve una parada a OTRA ruta — a la posición `toIndex`, o al final si no se
 * da (el drop entre rutas trae posición; otros llamadores no).
 *
 * La validación territorial es del llamador (`moveTargetsFor` decide a dónde se
 * puede); acá solo se ejecuta. `custom` se recalcula en las dos puntas, y la
 * ruta destino se abre para que se vea a dónde llegó.
 */
export function moveStopToRoute(
  from: ReviewRoute,
  index: number,
  to: ReviewRoute,
  toIndex?: number,
): void {
  if (from === to) return;
  const [moved] = from.stops.splice(index, 1);
  if (!moved) return;
  const at =
    toIndex === undefined ? to.stops.length : Math.max(0, Math.min(toIndex, to.stops.length));
  to.stops.splice(at, 0, moved);
  to.open = true;
  from.custom = !sameAsOriginal(from);
  to.custom = !sameAsOriginal(to);
}

/**
 * Restaura el orden del optimizador en UNA ruta.
 *
 * Con movimientos entre rutas, restaurar exige deshacerlos en las dos puntas —
 * si no, una visita quedaría duplicada (vuelve acá Y sigue allá) o perdida:
 * - las paradas AJENAS que esta ruta recibió vuelven a su ruta de origen (al
 *   final de ella);
 * - las paradas PROPIAS que se fueron a otra ruta se retiran de allá (acá las
 *   repone el original).
 */
export function resetReviewRoute(route: ReviewRoute, all: readonly ReviewRoute[] = []): void {
  const ownIds = new Set(route.original.map((s) => s.orderId));

  for (const stop of route.stops) {
    if (ownIds.has(stop.orderId)) continue;
    const owner = (all ?? []).find(
      (r) => r !== route && r.original.some((o) => o.orderId === stop.orderId),
    );
    if (owner && !owner.stops.some((s) => s.orderId === stop.orderId)) {
      owner.stops.push(stop);
      owner.custom = !sameAsOriginal(owner);
    }
  }

  for (const other of all ?? []) {
    if (other === route) continue;
    const before = other.stops.length;
    other.stops = other.stops.filter((s) => !ownIds.has(s.orderId));
    if (other.stops.length !== before) other.custom = !sameAsOriginal(other);
  }

  route.stops = route.original.map((s) => ({ ...s }));
  route.custom = false;
}

/**
 * Carga VIGENTE de la ruta (lb): la suma de sus paradas actuales.
 *
 * Se recalcula del estado y no se guarda: mover un pedido entre rutas la cambia
 * en las dos puntas, y un valor almacenado quedaría mintiendo tras el primer
 * movimiento — que es exactamente cuando más se necesita.
 */
export function reviewLoadLb(route: ReviewRoute): number {
  return route.stops.reduce((sum, s) => sum + (s.weightLb ?? 0), 0);
}

/**
 * Techo del DÍA del camión (lb): capacidad × viajes del plan. Es la misma vara
 * con la que el servicio juzga si la flota alcanza (`fleet_capacity`), no la de
 * un solo viaje: una ruta con recargas carga más que su capacidad nominal.
 */
export function reviewDayCapacityLb(route: ReviewRoute): number {
  return route.capacityLb * Math.max(1, route.trips);
}

/** Libras por ENCIMA del techo del día (0 si cabe). */
export function reviewOverloadLb(route: ReviewRoute): number {
  const capacity = reviewDayCapacityLb(route);
  if (capacity <= 0) return 0;
  return Math.max(0, reviewLoadLb(route) - capacity);
}

/** Cuántas rutas llevan orden personalizado, para declararlo en el botón. */
export function customCount(routes: readonly ReviewRoute[]): number {
  return (routes ?? []).filter((r) => r.custom).length;
}

/**
 * El cuerpo del POST, desde el orden VIGENTE.
 *
 * `sequence` se renumera 1..n por posición actual — es lo único que cambia al
 * personalizar; el contrato es el mismo. Los ids se limpian de los sufijos de
 * división (`#N` del backend, `__sN` del bin-packing) SIN fusionar ni descartar:
 * cada parte de un pedido dividido sigue siendo una visita real con su propia
 * `sequence`, igual que hacía la confirmación directa.
 */
export function toConfirmRoutes(routes: readonly ReviewRoute[]): ConfirmRoutePayload[] {
  return (routes ?? []).map((r) => ({
    subRouteId: r.subRouteId,
    stops: r.stops.map((s, i) => ({ orderId: basePlanOrderId(s.orderId), sequence: i + 1 })),
  }));
}

/* ------------------- Adoptar el orden de visita guardado ------------------- */

/**
 * Resuelve el **destino** de un pedido: cliente + dirección.
 *
 * `ReviewStop` no lo lleva —solo trae lo mínimo para identificar y arrastrar— así que
 * el llamador inyecta el mapeo. Mantiene este módulo puro: entran rutas y una función,
 * salen órdenes.
 *
 * La llave tiene que ser el PAR, porque así lo indexa el orden guardado
 * (`businessPartnerMongoId` + `businessPartnerAddressMongoId`). `customerIdOf` usa solo
 * el cliente, y para agrupar visitas alcanza; para casar contra el guardado, no.
 */
export type DestinationKeyOf = (baseOrderId: string) => string | undefined;

/**
 * La llave de un destino a partir de sus dos ids.
 *
 * Una sola función para los dos lados —el orden guardado de la API y el pedido del
 * plan— para que no puedan armarla distinto: si un lado usara otro separador o otro
 * orden de los campos, la comparación no encontraría nada y la adopción no aplicaría
 * nada **en silencio**, que es el peor modo de falla posible acá.
 */
export function destinationKey(
  businessPartnerMongoId?: unknown,
  businessPartnerAddressMongoId?: unknown,
): string {
  return `${String(businessPartnerMongoId ?? '')}|${String(businessPartnerAddressMongoId ?? '')}`;
}

/** El destino de una parada, resolviendo antes su sufijo de división. */
function destinationOfStop(stop: ReviewStop, keyOf: DestinationKeyOf): string | undefined {
  return keyOf(basePlanOrderId(stop.orderId));
}

/**
 * La secuencia de destinos de una ruta, **sin repetir**.
 *
 * Varios pedidos del mismo cliente son una sola parada —el camión para una vez— así que
 * el destino aparece una vez, en la posición de su primer pedido. Es la unidad en la que
 * el orden guardado está expresado, y por eso la unidad en la que se compara.
 */
export function destinationSequenceOf(
  route: ReviewRoute,
  keyOf: DestinationKeyOf,
): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const stop of route.stops) {
    const key = destinationOfStop(stop, keyOf);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(key);
  }
  return out;
}

/**
 * ¿El orden guardado difiere del propuesto? **Solo la permutación cuenta.**
 *
 * Se comparan las dos secuencias **restringidas a los destinos que comparten**. Con eso,
 * las otras dos formas de diferir dejan de contar solas:
 *
 * ```
 * guardado A B C D · plan A B C D E   →  A B C D  vs  A B C D   iguales → NO cambia
 * guardado A B C D · plan A B D       →  A B D    vs  A B D     iguales → NO cambia
 * guardado A B C D · plan A C B D     →  A C B D  vs  A B C D   distintos → aplica
 * ```
 *
 * El primer caso es el corriente —se guardó el orden a las 8 y entraron pedidos a las
 * 10— y aplicarlo no movería ninguna parada: solo encendería el indicador de orden
 * personalizado sin que nadie personalizara nada.
 */
export function savedOrderDiffers(
  route: ReviewRoute,
  savedKeys: readonly string[],
  keyOf: DestinationKeyOf,
): boolean {
  const plan = destinationSequenceOf(route, keyOf);
  const savedSet = new Set(savedKeys);
  const planSet = new Set(plan);
  const planShared = plan.filter((k) => savedSet.has(k));
  const savedShared = savedKeys.filter((k) => planSet.has(k));
  if (planShared.length !== savedShared.length) return false;
  return planShared.some((k, i) => k !== savedShared[i]);
}

/** Destinos del orden guardado que ya no están en el plan. Se declara, no se calla. */
export function missingSavedDestinations(
  route: ReviewRoute,
  savedKeys: readonly string[],
  keyOf: DestinationKeyOf,
): number {
  const plan = new Set(destinationSequenceOf(route, keyOf));
  return savedKeys.filter((k) => !plan.has(k)).length;
}

/**
 * Reordena las paradas según el orden guardado, **conservando al motor donde el humano
 * no opinó**.
 *
 * A cada parada se le da un rango:
 * - si su destino está en el guardado → la posición que tiene ahí;
 * - si no → el rango del último destino guardado que la precede, así queda **justo
 *   después** de él. Un pedido que entró más tarde ya tiene una posición calculada
 *   contra la matriz del mapa; mandarlo al final del día sería tirar ese cálculo.
 *
 * El ordenamiento es **estable** (desempata por la posición previa), y eso es lo que
 * mantiene contiguos los pedidos de un mismo destino — incluidas las partes de una
 * visita dividida, que comparten destino por construcción.
 */
export function applySavedOrder(
  route: ReviewRoute,
  savedKeys: readonly string[],
  keyOf: DestinationKeyOf,
): void {
  const rankOf = new Map(savedKeys.map((k, i) => [k, i]));
  // Antes del primer ancla guardada, `-1`: esas paradas se van al frente, que es
  // donde el motor las había puesto.
  let anchor = -1;
  const ranked = route.stops.map((stop, index) => {
    const key = destinationOfStop(stop, keyOf);
    const saved = key !== undefined ? rankOf.get(key) : undefined;
    if (saved !== undefined) anchor = saved;
    return { stop, index, rank: saved ?? anchor };
  });
  ranked.sort((a, b) => a.rank - b.rank || a.index - b.index);
  route.stops = ranked.map((r) => r.stop);
}

/* ------- Restaurar EN QUE RUTA va cada pedido, desde el orden guardado ------- *
 *
 * `applySavedOrder` restaura la SECUENCIA dentro de una ruta. Esto restaura la
 * PERTENENCIA: trae cada destino a la ruta que su orden guardado declara.
 *
 * Hacían falta las dos. `savedOrderDiffers` compara las secuencias restringidas a
 * los destinos compartidos, así que un pedido movido a otra ruta le resultaba
 * invisible —las dos rutas informaban "ya coincidían" y la acción no hacía nada—.
 * Ese recorte es correcto para el caso corriente ("se guardó a las 8 y entraron
 * pedidos a las 10") pero trataba igual dos causas que no son la misma:
 *
 *   ya no está en el día   → nada que restaurar
 *   está en OTRA ruta      → recuperable, y es lo que esto hace
 */

/**
 * Qué pasó al restaurar la pertenencia.
 *
 * Las unidades van en el nombre a propósito: lo que se mueve son PARADAS y lo que
 * falta o se contradice son DESTINOS. Un solo número que mezclara las dos —como el
 * `missing` que esto reemplaza— obliga a adivinar qué mide.
 */
export interface SavedReclaimResult {
  /** Paradas que volvieron a la ruta que su orden guardado declara. */
  reclaimedStops: number;
  /** Paradas que no volvieron: esa ruta no visita el territorio del cliente. */
  blockedStops: number;
  /** Destinos que dos órdenes guardados reclaman a la vez. No se mueven. */
  conflictedDestinations: number;
  /**
   * Destinos del orden guardado que no están en ninguna ruta **ni entre los no
   * asignados**. Esos sí ya no están en el día.
   *
   * Antes contaba solo contra las rutas, así que un destino visible en el pool se
   * declaraba ausente — y el recibo lo afirmaba con esas palabras.
   */
  goneDestinations: number;
  /**
   * Sub-rutas que RECIBIERON al menos una parada.
   *
   * Las necesita el aviso de pisada: una ruta que recibió lo que su orden guardado
   * reclamaba pasa a guardar ESE orden, así que advertir que lo va a pisar sería
   * falso — el mismo criterio que ya rige para las que se reordenaron.
   */
  reclaimedInto: string[];
  /**
   * Sub-rutas con al menos un destino guardado que quedó en OTRA ruta —bloqueado por
   * territorio o en conflicto—.
   *
   * Las necesita el resumen: una ruta así NO "ya coincidía", aunque su secuencia
   * restringida a los destinos compartidos sea idéntica. Decir que coincidía cuando
   * le falta un destino que el guardado sí tiene es la contradicción que este cambio
   * viene a sacar.
   */
  unresolved: string[];
}

/**
 * Trae cada destino a la ruta que su orden guardado declara.
 *
 * **Global, no ruta por ruta.** Un destino que la ruta A reclama puede estar hoy en
 * B, así que resolver solo A lo dejaría en las dos —una visita que dos camiones
 * creen suya— o lo perdería. Se arma el mapa completo de reclamos y recién después
 * se mueve, igual que `applyReviewOrderDelta`.
 *
 * Corre ANTES de reordenar: reordenar sobre un contenido que todavía va a cambiar
 * deja posiciones calculadas contra otra lista. Como efecto lateral útil, después
 * del reclamo la secuencia de la ruta ya contiene el destino, así que
 * `savedOrderDiffers` vuelve a ser una pregunta bien planteada.
 *
 * La unidad es el DESTINO y no la parada: las partes de una visita dividida
 * comparten destino por construcción, y traer una sola partiría la visita entre dos
 * camiones. El orden de visita se indexa por destino y no puede representar eso.
 *
 * @param canReceive la regla territorial, inyectada. Ver `canRouteReceive`: el
 *   gestor no puede producir una asignación que el arrastre habría rechazado, así
 *   que un destino cuya ruta guardada ya no visita su territorio NO se trae y se
 *   declara — lo que hay que arreglar es el maestro, y para eso hay que enterarse.
 */
export function reclaimSavedDestinations(
  routes: ReviewRoute[],
  savedByRoute: ReadonlyMap<string, readonly string[]>,
  keyOf: DestinationKeyOf,
  canReceive: (subRouteId: string, stop: ReviewStop) => boolean,
  /**
   * Las paradas de un destino que hoy está entre los **no asignados**.
   *
   * Sin esto, un destino que el guardado nombra y que no está en ninguna ruta se contaba
   * como `goneDestinations` —"ya no está en el día"— cuando en realidad estaba a la
   * vista, en el pool. El caso llega por un camino concreto: un pedido incorporado a
   * mano y confirmado, sobre una corrida nueva que no lo trae en su plan.
   *
   * Devuelve TODAS las paradas del destino: la unidad de esta función es el destino y no
   * la parada, y traer una sola partiría una visita dividida entre dos camiones.
   */
  fromPool?: (destKey: string) => readonly ReviewStop[] | undefined,
): SavedReclaimResult {
  const result: SavedReclaimResult = {
    reclaimedStops: 0,
    blockedStops: 0,
    conflictedDestinations: 0,
    goneDestinations: 0,
    reclaimedInto: [],
    unresolved: [],
  };
  const reclaimedInto = new Set<string>();
  const unresolved = new Set<string>();
  if (!routes?.length || !savedByRoute?.size) return result;

  const byId = new Map(routes.map((r) => [r.subRouteId, r]));

  // Quién reclama cada destino. Un destino que aparece en DOS guardados queda en
  // conflicto: los dos viajes lo registran como propio y elegir uno sobre el otro no
  // tiene base. Se anota y no se mueve.
  const claimedBy = new Map<string, string>();
  const conflicted = new Set<string>();
  for (const [subRouteId, keys] of savedByRoute) {
    if (!byId.has(subRouteId)) continue;
    for (const key of keys ?? []) {
      if (!key) continue;
      const already = claimedBy.get(key);
      if (already !== undefined && already !== subRouteId) {
        conflicted.add(key);
        continue;
      }
      claimedBy.set(key, subRouteId);
    }
  }
  result.conflictedDestinations = conflicted.size;

  // Dónde está hoy cada destino. Se recorre TODO el gestor: un destino puede estar
  // repartido entre dos rutas si alguien arrastró una sola parte de una visita
  // dividida, y el reclamo lo junta.
  const located = new Map<string, Array<{ stop: ReviewStop; route: ReviewRoute }>>();
  for (const route of routes) {
    for (const stop of route.stops) {
      const key = destinationOfStop(stop, keyOf);
      if (!key) continue;
      const list = located.get(key);
      if (list) list.push({ stop, route });
      else located.set(key, [{ stop, route }]);
    }
  }

  for (const [key, owner] of claimedBy) {
    if (conflicted.has(key)) {
      // El destino queda donde está, así que la ruta que lo reclama sigue sin él.
      if (located.get(key)?.some((f) => f.route.subRouteId !== owner)) unresolved.add(owner);
      continue;
    }
    const target = byId.get(owner);
    if (!target) continue;
    const found = located.get(key);
    if (!found?.length) {
      // No está en ninguna ruta: puede estar entre los no asignados. El guardado lo
      // nombra, así que es una asignación que el operador ya hizo y confirmó.
      const desdePool = fromPool?.(key) ?? [];
      if (!desdePool.length) {
        // Ahora sí: no está en las rutas NI en el pool. Eso es "ya no está en el día".
        result.goneDestinations++;
        continue;
      }
      // La regla territorial SÍ se aplica acá, a diferencia de restaurar la foto:
      // traerlo es una asignación NUEVA, la dispara el operador, y el maestro de
      // clientes ya está cargado hace rato.
      if (!canReceive(owner, desdePool[0])) {
        result.blockedStops += desdePool.length;
        unresolved.add(owner);
        continue;
      }
      for (const stop of desdePool) {
        target.stops.push(stop);
        result.reclaimedStops++;
        reclaimedInto.add(owner);
      }
      refreshReviewCustom(target);
      continue;
    }
    const strays = found.filter((f) => f.route !== target);
    if (!strays.length) continue;
    // La regla territorial se evalúa una vez por destino: todas sus paradas son del
    // mismo cliente, así que la respuesta no puede diferir entre ellas.
    if (!canReceive(owner, strays[0].stop)) {
      result.blockedStops += strays.length;
      unresolved.add(owner);
      continue;
    }
    const touched = new Set<ReviewRoute>([target]);
    for (const { stop, route } of strays) {
      const at = route.stops.indexOf(stop);
      if (at < 0) continue;
      route.stops.splice(at, 1);
      // Al final: la fase de reordenamiento le da su posición guardada, y el orden
      // estable mantiene contiguas las partes de una visita dividida.
      target.stops.push(stop);
      touched.add(route);
      result.reclaimedStops++;
      reclaimedInto.add(owner);
    }
    for (const route of touched) refreshReviewCustom(route);
  }

  result.reclaimedInto = [...reclaimedInto];
  result.unresolved = [...unresolved];
  return result;
}

/**
 * Recalcula si la ruta lleva orden personalizado, comparándola con la propuesta del
 * optimizador.
 *
 * Existe porque adoptar el orden guardado **afirmaba** `custom = true` donde su
 * propio comentario decía que lo recalculaba. Un orden guardado puede coincidir con
 * el del motor: si el operador había reordenado a mano y adoptar lo devuelve a la
 * propuesta, la ruta NO tiene orden personalizado, y decir que sí es falso.
 *
 * Y era inestable además de falso: `applyReviewOrderDelta` sí recalcula al restaurar
 * la foto, así que la insignia desaparecía al cerrar y reabrir el diálogo — el mismo
 * estado descrito de dos formas según si cerraste.
 *
 * Se exporta en vez de que el llamador compare: `sameAsOriginal` es la definición de
 * "igual a la propuesta" y tiene que haber una sola.
 */
export function refreshReviewCustom(route: ReviewRoute): void {
  route.custom = !sameAsOriginal(route);
}
